import {lazy, memo} from 'react';
import {createBrowserRouter, Navigate, RouterProvider} from 'react-router-dom';

import {WellDesignPath} from '@features/wellDesign/route';
import Layout from './components/Layout';
import {MonitoringType} from '@pages/newMonitoring/types';
import {GuardedRoute} from '@ui/GuardedRoute/GuardedRoute';
import {Routes as RoutesLinks} from './constants/routes';
import {Permission} from './utils/hooks/permissions/usePermissions';
import withSuspense from './utils/hoc/withSuspense';

import 'styles/app.scss';
import {ADMIN_ROUTES} from '@pages/AdministrationPage/constants';
import {MonitoringTabTypes} from '@pages/newMonitoring/components/NavTabs/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const CandleConstruction = lazy(
    () => import('@features/autoDrilling/CandleConstruction/CandleConstruction'),
);
const PROMaster = lazy(() => import('@features/autoDrilling/PROMaster/PROMaster'));
const SlideSheet = lazy(() => import('@features/autoDrilling/SlideSheet/SlideSheet'));
const DrillBitAnalyticsPage = lazy(
    () => import('@pages/DrillBitAnalyticsPage/DrillBitAnalyticsPage'),
);
const MonitoringPage = lazy(() => import('@pages/newMonitoring/Dashboard'));
const TrajectoryTablePage = lazy(() => import('@pages/TrajectoryTablePage'));
const SMBOPage = lazy(() => import('@pages/SMBOPage'));
const SMBOViewPage = lazy(() => import('@pages/SMBOViewPage'));
const Page = lazy(() => import('@pages/TablePage'));
const FilesPage = lazy(() => import('@pages/FilesPage/FilesPage'));
const WellsPage = lazy(() => import('@pages/WellsPage/WellsPage'));
const CalendarPage = lazy(() => import('@pages/CalendarPage/CalendarPage'));
const PricesPage = lazy(() => import('@pages/PricesPage/PricesPage'));
const PricePage = lazy(() => import('@features/ratesAndPrices/PricesContent'));
const KnbkElementsPage = lazy(() => import('@pages/Knbkpage/KnbkElementsPage'));
const KnbkGroupsPage = lazy(() => import('@pages/Knbkpage/KnbkGroupsPage'));
const InstrumentMeasurePage = lazy(
    () => import('@pages/InstrumentMeasurePage/InstrumentMeasurePage'),
);
const WellDesign = lazy(() => import('@features/wellDesign/WellDesign'));
const DashboardPage = lazy(() => import('@pages/DashboardPage/DashboardPage'));
const TimeBalanceContent = lazy(
    () => import('@pages/DashboardPage/TimeBalance/TimeBalanceContent'),
);
const DashboardContent = lazy(
    () => import('@pages/DashboardPage/DashboardContent/DashboardContent'),
);
const MainPage = lazy(() => import('@pages/MainPage/MainPage'));
const ProjectsPage = lazy(() => import('@pages/ProjectsPage/ProjectsPage'));
const CcdpGroupsPage = lazy(() => import('@pages/CcdpPage/CcdpGroupsPage'));
const CcdpElementsPage = lazy(() => import('@pages/CcdpPage/CcdpElementsPage'));
const KpodpGroupsPage = lazy(() => import('@pages/KpodpPage/KpodpGroupsPage'));
const KpodpElementsPage = lazy(() => import('@pages/KpodpPage/KpodpElementsPage'));
const GroupedPages = lazy(() => import('@pages/GroupedPages'));
const RatesAndPricesPage = lazy(() => import('@pages/RatesAndPricesPage/RatesAndPricesPage'));
const CommonInfoPage = lazy(() => import('@pages/CommonInfoPage/CommonInfoPage'));
const GroupedPagesProject = lazy(() => import('@pages/GroupedPages/project/GroupedPagesProject'));
const KnbkGisGroupsPage = lazy(() => import('@pages/KnbkGisPage/KnbkGisGroupsPage'));
const KnbkGisElementsPage = lazy(() => import('@pages/KnbkGisPage/KnbkGisElementsPage'));
const GroupedPagesLessonsDatabase = lazy(
    () => import('@pages/GroupedPages/lessonsDatabase/GroupedPagesLessonsDatabase'),
);
const LessonsDatabasePage = lazy(() => import('@pages/LessonsDatabasePage/LessonsDatabasePage'));
const MonitoringWells = lazy(() => import('@pages/MonitoringWells/MonitoringWells'));
const GgdPage = lazy(() => import('@pages/GgdPage/GgdPage'));
const StageAnalyticsPage = lazy(() => import('@pages/StageAnalyticsPage/StageAnalyticsPage'));
const DRMPage = lazy(() => import('@pages/AutoDrillingPage/DRMPage'));
const CrewEfficiency = lazy(() => import('@features/crewEfficiency/CrewEfficiency'));
/* const MonitoringLayout = lazy(() => import('@pages/monitoring/components/Layout/MonitoringLayout'));
 */
const MonitoringPageWrapper = lazy(() => import('@pages/newMonitoring/MonitoringPageWrapper'));
const GroupedPagesDrillingCarpet = lazy(
    () => import('@pages/GroupedPages/drillingCarpet/GroupedPagesDrillingCarpet'),
);
const DrillingCarpetPage = lazy(() => import('@pages/DrillingCarpetPage/AutoDrillingPage'));
const EquipmentViewPage = lazy(() => import('@pages/EquipmentViewPage/EquipmentViewPage'));
const MomentAndWeightLoader = lazy(() => import('@features/momentAndWeight/MomentAndWeightLoader'));
const KnbkProjectGroupsPage = lazy(() => import('@pages/KnbkProjectPage/KnbkProjectGroupsPage'));
const KnbkProjectElementsPage = lazy(
    () => import('@pages/KnbkProjectPage/KnbkProjectElementsPage'),
);
const AdministrationPage = lazy(() => import('@pages/AdministrationPage/AdministrationPage'));
const WellCommonDashboardPage = lazy(
    () => import('@pages/WellCommonDashboardPage/WellCommonDashboardPage'),
);
const DDControlPage = lazy(() => import('@pages/DDControlPage/DDControlPage'));

const WellsQuantityPage = lazy(() => import('@pages/WellsQuantityPage/WellsQuantityPage'));
const NoMatchPage = lazy(() => import('@pages/NoMatchPage/NoMatchPage'));
const MudAnalyticsPage = lazy(() => import('@pages/MudAnalyticsPage/MudAnalyticsPage'));

const MyResourcesPage = lazy(() => import('@pages/MyResources/MyResourcesPage/MyResourcesPage'));
const CompanyPageWrapper = lazy(
    () => import('@pages/MyResources/CompanyPageWrapper/CompanyPageWrapper'),
);
const CompanyInfoPage = lazy(() => import('@pages/MyResources/CompanyInfo/CompanyInfo'));
const PersonalPage = lazy(() => import('@pages/MyResources/PersonalPage/PersonalPage'));
const StoragePage = lazy(() => import('@pages/MyResources/StoragePage/StoragePage'));
const PostsPage = lazy(() => import('@pages/MyResources/PostPage/PostPage'));
const PostsSchedulePage = lazy(
    () => import('@pages/MyResources/PostsSchedulePage/PostsSchedulePage'),
);
const EmployeePage = lazy(() => import('@pages/MyResources/EmployeePage/EmployeePage'));
const DrillingDatesPage = lazy(() => import('@pages/DrillingDatesPage/DrillingDatesPage'));
const DrillingRigInWellPage = lazy(
    () => import('@pages/DrillingRigInWellPage/DrillingRigInWellPage'),
);
const ResourcePage = lazy(() => import('@pages/MyResources/ResourcePage/ResourcePage'));
const WellRisksPage = lazy(() => import('@pages/WellRisksPage/WellRisksPage'));
const DataManagerPage = lazy(() => import('@pages/DataManagerPage/DataManagerPage'));
const MiniAppPage = lazy(() => import('@pages/MiniAppPage/MiniAppPage'));
const ResourceNptPage = lazy(() => import('@pages/MyResources/ResourcePage/ResourceNptPage'));

export const router = createBrowserRouter([
    {
        path: '',
        element: <Layout hasSidebar={true} />,
        children: [
            {
                path: '',
                element: withSuspense(GroupedPages),
                children: [
                    {
                        path: `${RoutesLinks.Files}/:wellId/:pageId/:id`,
                        element: withSuspense(FilesPage),
                    },
                    {
                        path: `${RoutesLinks.Tables}/:wellId?/:pageId?/:data?`,
                        element: withSuspense(Page),
                    },
                    {
                        path: `${RoutesLinks.Knbk}/:wellId/:pageId/`,
                        element: withSuspense(KnbkGroupsPage),
                    },
                    {
                        path: `${RoutesLinks.DrillingRigInWell}/:wellId/:pageId`,
                        element: withSuspense(DrillingRigInWellPage),
                    },
                    {
                        path: `${RoutesLinks.InstrumentMeasure}/:wellId/:pageId`,
                        element: withSuspense(InstrumentMeasurePage),
                    },
                    {
                        path: `${RoutesLinks.Knbk}/:wellId/:pageId/:pageType/:groupId`,
                        element: withSuspense(KnbkElementsPage),
                    },
                    {
                        path: `${RoutesLinks.KnbkGis}/:wellId/:pageId/`,
                        element: withSuspense(KnbkGisGroupsPage),
                    },
                    {
                        path: `${RoutesLinks.KnbkGis}/:wellId/:pageId/:groupId`,
                        element: withSuspense(KnbkGisElementsPage),
                    },
                    {
                        path: `${RoutesLinks.Ccdp}/:wellId/:pageId`,
                        element: withSuspense(CcdpGroupsPage),
                    },
                    {
                        path: `${RoutesLinks.Ccdp}/:wellId/:pageId/:groupId`,
                        element: withSuspense(CcdpElementsPage),
                    },
                    {
                        path: `${RoutesLinks.Kpodp}/:wellId/:pageId`,
                        element: withSuspense(KpodpGroupsPage),
                    },
                    {
                        path: `${RoutesLinks.Kpodp}/:wellId/:pageId/:groupId`,
                        element: withSuspense(KpodpElementsPage),
                    },
                    {
                        path: `${RoutesLinks.Calendar}/:wellId/:pageId`,
                        element: withSuspense(CalendarPage),
                    },
                    {
                        path: `${RoutesLinks.Ggd}/:wellId/:pageId`,
                        element: withSuspense(GgdPage),
                    },
                    {
                        path: `${RoutesLinks.MudAnalytics}/:wellId/:pageId`,
                        element: withSuspense(MudAnalyticsPage),
                    },
                    {
                        path: `${RoutesLinks.WellRisks}/:wellId/:pageId`,
                        element: withSuspense(WellRisksPage),
                    },
                    {
                        path: `${RoutesLinks.Dashboard}/:wellId?`,
                        element: withSuspense(DashboardPage),
                        children: [
                            {
                                index: true,
                                element: withSuspense(DashboardContent),
                            },
                            {
                                path: 'time-balance',
                                element: withSuspense(TimeBalanceContent),
                            },
                            {
                                path: WellDesignPath,
                                element: withSuspense(WellDesign),
                            },
                        ],
                    },
                    {
                        path: `${RoutesLinks.Trajectory}/:wellId/:pageId/:groupId?`,
                        element: withSuspense(TrajectoryTablePage),
                    },
                    {
                        path: `${RoutesLinks.WellCommonDashboard}/:wellId`,
                        element: withSuspense(WellCommonDashboardPage),
                    },
                ],
            },
        ],
    },
    {
        path: '',
        children: [
            {
                path: '',
                element: <GuardedRoute permission={Permission.CAN_VIEW_MONITORING}></GuardedRoute>,
                children: [
                    {
                        path: '',
                        element: <Layout hasSidebar={false} />,
                        children: [
                            {
                                path: `${RoutesLinks.Monitoring}/`,
                                element: withSuspense(MonitoringWells),
                            },
                        ],
                    },
                    {
                        path: '',
                        element: withSuspense(MonitoringPageWrapper),
                        children: [
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_DRILLING_EFFICIENCY}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.EFICIENCY}`,
                                        element: withSuspense(CrewEfficiency),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_TND_TAB}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.MOMENT_AND_WEIGHT}`,
                                        element: withSuspense(MomentAndWeightLoader),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_LAS_TAB}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.LAS}`,
                                        element: withSuspense(MonitoringPage, {
                                            type: MonitoringType.las,
                                        }),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_LAS_TAB}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.LAS}/:lasId?`,
                                        element: withSuspense(MonitoringPage, {
                                            type: MonitoringType.las,
                                        }),
                                    },
                                ],
                            },
                            {
                                id: 'rt-monitoring-page',
                                path: `${RoutesLinks.Monitoring}/:wellId/:day?`,
                                element: withSuspense(MonitoringPage, {
                                    type: MonitoringType.realTime,
                                }),
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_CANDLE_CONSTRUCTION_VIEW}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/${MonitoringTabTypes.CANDLE_CONSTRUCTION}`,
                                        element: withSuspense(CandleConstruction),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_DRM_VIEW}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.DRM}`,
                                        element: withSuspense(DRMPage),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_DIGITAL_RIG_VIEW}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.PRO_MASTER}`,
                                        element: withSuspense(PROMaster),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_MONITORING} // TODO: change to CAN_VIEW_DD_CONTROL
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.DD_CONTROL}`,
                                        element: withSuspense(DDControlPage),
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <GuardedRoute
                                        permission={Permission.CAN_VIEW_SLIDE_SHEET}
                                    ></GuardedRoute>
                                ),
                                children: [
                                    {
                                        path: `${RoutesLinks.Monitoring}/:wellId/${MonitoringTabTypes.SLIDE_SHEET}`,
                                        element: withSuspense(SlideSheet),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },

    {
        path: '',
        element: <Layout hasSidebar={false} />,
        children: [
            {
                path: '',
                element: <GuardedRoute permission={Permission.Projects}></GuardedRoute>,
                children: [
                    {
                        path: '',
                        element: withSuspense(GroupedPagesProject),
                        children: [
                            {
                                path: `${RoutesLinks.Projects}/tables/:projectId/:pageId?/:data?`,
                                element: withSuspense(Page),
                            },
                            {
                                path: `${RoutesLinks.KnbkProject}/:projectId/:pageId/`,
                                element: withSuspense(KnbkProjectGroupsPage),
                            },
                            {
                                path: `${RoutesLinks.KnbkProject}/:projectId/:pageId/:groupId`,
                                element: withSuspense(KnbkProjectElementsPage),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '',
        element: <Layout hasSidebar={false} />,
        children: [
            {
                index: true,
                path: RoutesLinks.Main,
                element: withSuspense(MainPage),
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.CAN_VIEW_MINIAPPS}></GuardedRoute>,
                children: [
                    {
                        path: RoutesLinks.Miniapp,
                        element: withSuspense(MiniAppPage),
                    },
                ],
            },
            {
                path: '',
                element: (
                    <GuardedRoute
                        permission={[
                            Permission.Wells,
                            Permission.CAN_VIEW_WELL_HEADER,
                            Permission.CAN_EDIT_GRP,
                        ]}
                    ></GuardedRoute>
                ),
                children: [
                    {
                        path: RoutesLinks.Wells,
                        element: withSuspense(WellsPage),
                    },
                ],
            },
            {
                path: '',
                children: [
                    {
                        path: RoutesLinks.DrillBitAnalytics,
                        element: withSuspense(DrillBitAnalyticsPage),
                    },
                ],
            },

            {
                path: '',
                element: (
                    <GuardedRoute permission={Permission.CAN_VIEW_FILE_MANAGER}></GuardedRoute>
                ),
                children: [
                    {
                        path: RoutesLinks.DataManager,
                        element: withSuspense(DataManagerPage),
                    },
                ],
            },

            {
                path: '',
                element: (
                    <GuardedRoute
                        permission={[Permission.Wells, Permission.CAN_VIEW_WELL_HEADER]}
                    ></GuardedRoute>
                ),
                children: [
                    {
                        path: RoutesLinks.WellsQuantity,
                        element: withSuspense(WellsQuantityPage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.Projects}></GuardedRoute>,
                children: [
                    {
                        path: RoutesLinks.Projects,
                        element: withSuspense(ProjectsPage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.Prices}></GuardedRoute>,
                children: [
                    {
                        path: RoutesLinks.RatesAndPrices,
                        element: withSuspense(RatesAndPricesPage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.Prices}></GuardedRoute>,
                children: [
                    {
                        path: `${RoutesLinks.Prices}/:contractId`,
                        element: withSuspense(PricesPage),
                    },
                    {
                        path: `${RoutesLinks.Price}`,
                        element: withSuspense(PricePage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.CommonInfo}></GuardedRoute>,
                children: [
                    {
                        path: `${RoutesLinks.CommonInfo}/:wellId?`,
                        element: withSuspense(CommonInfoPage),
                    },
                    {
                        path: `${RoutesLinks.StageAnalytics}/:wellId?`,
                        element: withSuspense(StageAnalyticsPage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.Wells}></GuardedRoute>,
                children: [
                    {
                        path: `${RoutesLinks.DrillingDates}`,
                        element: withSuspense(DrillingDatesPage),
                    },
                ],
            },
            {
                path: '',
                element: (
                    <GuardedRoute permission={Permission.CanViewIncidentAnalyse}></GuardedRoute>
                ),
                children: [
                    {
                        path: '',
                        element: withSuspense(GroupedPagesLessonsDatabase),
                        children: [
                            {
                                path: `${RoutesLinks.LessonsDatabase}/:pageId`,
                                element: withSuspense(LessonsDatabasePage),
                            },
                        ],
                    },
                ],
            },
            {
                path: '',
                element: (
                    <GuardedRoute permission={Permission.CAN_VIEW_DRILLING_CARPET}></GuardedRoute>
                ),
                children: [
                    {
                        path: '',
                        element: withSuspense(GroupedPagesDrillingCarpet),
                        children: [
                            {
                                path: `${RoutesLinks.DrillingCarpet}/:pageId`,
                                element: withSuspense(DrillingCarpetPage),
                            },
                        ],
                    },
                ],
            },
            {
                path: '',
                element: (
                    <GuardedRoute permission={Permission.CAN_VIEW_DRILLING_RIG}></GuardedRoute>
                ),
                children: [
                    {
                        path: RoutesLinks.SMBO,
                        element: withSuspense(SMBOPage),
                    },
                    {
                        path: `${RoutesLinks.DrillingRig}/:rigId/smbo`,
                        element: withSuspense(SMBOViewPage),
                    },
                    {
                        path: `${RoutesLinks.DrillingRig}/:rigId/equipment/:equipmentId`,
                        element: withSuspense(EquipmentViewPage),
                    },
                ],
            },
            {
                path: '',
                element: <GuardedRoute permission={Permission.CAN_VIEW_ADMIN_PANEL}></GuardedRoute>,
                children: [
                    {
                        path: RoutesLinks.Administration,
                        element: withSuspense(AdministrationPage),
                        children: [
                            {path: '', element: <Navigate to={ADMIN_ROUTES[0].path} />},
                            ...ADMIN_ROUTES,
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '',
        element: <GuardedRoute permission={Permission.CAN_VIEW_MY_RESOURCES}></GuardedRoute>,
        children: [
            {
                path: '',
                element: <Layout hasSidebar={false} />,
                children: [
                    {
                        path: RoutesLinks.MyResources,
                        element: withSuspense(MyResourcesPage),
                    },
                ],
            },
        ],
    },
    {
        path: '',
        element: <GuardedRoute permission={Permission.CAN_VIEW_MY_RESOURCES}></GuardedRoute>,
        children: [
            {
                path: '',
                element: withSuspense(CompanyPageWrapper),
                children: [
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.ResourceCommonDashboard}`,
                        element: withSuspense(CompanyInfoPage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Staff}`,
                        element: withSuspense(PersonalPage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Storage}`,
                        element: withSuspense(StoragePage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Posts}`,
                        element: withSuspense(PostsSchedulePage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Posts}/:postId`,
                        element: withSuspense(PostsPage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Staff}/:employeeId`,
                        element: withSuspense(EmployeePage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Storage}/:resourceId`,
                        element: withSuspense(ResourcePage),
                    },
                    {
                        path: `${RoutesLinks.MyResources}/:companyId${RoutesLinks.Storage}/:resourceId${RoutesLinks.ResourcesNpt}`,
                        element: withSuspense(ResourceNptPage),
                    },
                ],
            },
        ],
    },

    {
        path: '*',
        element: <NoMatchPage />,
    },
]);

const App = memo(() => {
    return <RouterProvider router={router} />;
});

export default App;
